import {MBusinessDetailGraphEntity} from 'app/modules/discovery/core/_models'
import {IUserFE} from 'interfaces/IUserFE'
import React, {cloneElement} from 'react'

import {updateUserAsncThunk} from 'redux/actions/userActions'
import {AppDispatch} from 'redux/store'

import {stateNameCodes} from 'utilities/stateCodes'
import * as yup from 'yup'

//send in:
//{entity_type: 'business',
//filters: [
//{field:'state_province', fieldValue:'IN'},
//{field:'state_province', fieldValue: 50000, comparatorField: 'lt'},
//{entityType:'award',entityField:'amount', entityValue: 50000, comparatorField: 'lt'},
//{existsField:'award', fieldValue: true},
//{entityType:'award',entityField: 'phase', entityValue: 'Phase II'},
//]
type KeysOfBusiness = keyof MBusinessDetailGraphEntity

const schema = yup.object().shape({
  address_postal_code: yup
    .string()
    .required()
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(5, 'Must be exactly 5 digits')
    .max(5, 'Must be exactly 5 digits'),
})

interface iFilterVals {
  field: string
  existsField?: string
  comparatorField?: '<=' | '<' | '>' | '>='
  fieldValue: string | boolean | number
  entityType?: string
  entityField?: string
  entityValue?: string | boolean | number
}
export interface IFilterControlOptions {
  entityType: string
  fieldName: string
  fieldValue?: boolean | string | number | any
  fieldDisplay: string
  fieldOperator?: string
  fieldNestedName?: string
  fieldNestedDefaultValue?: string
  wildcard?: string
  fieldDefaultValue?: string
  type:
    | 'selectbox'
    | 'textbox'
    | 'checkbox'
    | 'radio'
    | 'autocomplete'
    | 'range'
    | 'divider'
    | 'multichip'
    | 'dategtlt'
  rank: number
  min?: number
  max?: number
  options?: {key: string | number; value: string}[]
  ordered?: 'descending' | 'ascending'
  // validationSchema?: yup.ObjectSchema<
  //   Assign<
  //     ObjectShape,
  //     {
  //       address_postal_code: RequiredStringSchema<string | undefined, AnyObject>
  //     }
  //   >,
  //   AnyObject,
  //   TypeOfShape<any>,
  //   AssertsShape<any>
  // >
}
export interface IStateFilter {
  value: string | number | boolean | any
  display: string
}

export const formatFiltersDisplay = (filters?: IFilterControlOptions[]) => {
  const filterObj: Record<string, IStateFilter> = {}
  if (filters && filters.filter((f) => f.fieldValue)?.length) {
    filters.map((m) => {
      if (m.fieldValue) {
        //mainly for awards right now, and checking for boolean for checkbox issue sending a default ida query as true instad of ""
        if (m.fieldName.includes('^')) {
          const splitFieldName = m.fieldName.split('^')
          if (splitFieldName.length === 2) {
            filterObj[`${splitFieldName[1]}`] = {
              display: m.fieldDisplay,
              value: {
                [m.fieldNestedName ?? 'n/a']:
                  m.fieldValue && typeof m.fieldValue !== 'boolean'
                    ? m.fieldValue
                    : m.fieldNestedDefaultValue,
              },
            }
          }
        } else if (m.fieldOperator) {
          filterObj[`${m.fieldName}${m.fieldOperator}`] = {
            value: m.fieldValue,
            display: m.fieldDisplay,
          }
        } else if (m.wildcard) {
          filterObj[`${m.fieldName}${m.wildcard}`] = {
            value: m.fieldValue,
            display: m.fieldDisplay,
          }
        } else {
          filterObj[m.fieldName] = {value: m.fieldValue, display: m.fieldDisplay}
        }
      }
    })
  }
  return filterObj
}

//order takes an array [], fieldname plus operator like  "order": ["name+"], for ascending, or "order": ["total_revenue-"] for decending,
// then for fuzzy match:   "order": ["name~strategic"], and for concept aliases: "order": ["concept_aliases.name~Artoficial Ontelligence"],
export const formatFuzzyOrder = (filtersToOrders?: IFilterControlOptions[]) => {
  const orderArr: string[] = []
  if (filtersToOrders && filtersToOrders.filter((f) => f.fieldValue)?.length) {
    //only get ones with values
    filtersToOrders
      .filter((f) => f.fieldValue)
      .map((m) => {
        if (m.ordered) {
          orderArr.push(`${m.fieldName}${m.ordered === 'descending' ? '-' : '+'}`)
        }
        //check if concept
        if (m.entityType === 'concept') {
          orderArr.push(`concept_aliases.${m.fieldName}~${m.fieldValue}`)
        } else {
          //check carrot
          if (m.fieldName.includes('^')) {
            const splitFieldName = m.fieldName.split('^')
            if (splitFieldName.length === 2) {
              orderArr.push(`${splitFieldName[1]}~${m.fieldValue}`)
            }
          } else {
            orderArr.push(`${m.fieldName}~${m.fieldValue}`)
          }
        }
      })
  }
  return orderArr
}

export const formatFiltersFetch = (filters?: Record<string, IStateFilter>) => {
  const filterObj: Record<string, any> = {}
  if (filters && Object.keys(filters)?.length) {
    Object.entries(filters).map(([k, v]) => {
      if (k === 'multi_university' && Array.isArray(v.value)) {
        filterObj['business'] = {['id']: v.value.map((str) => str)}
      } else {
        filterObj[k] = v.value
      }
    })
  }
  return filterObj
}
interface iAddData {
  forType: string
  values: {
    key: string
    value: string
  }[]
}

export const saveFiltersCF = async (
  filters: IFilterControlOptions[],
  filterType: string,
  reduxUser: IUserFE,
  setStateFilters: React.Dispatch<
    React.SetStateAction<{type: string; filters: Record<string, IStateFilter>}[]>
  >,
  dispatch: AppDispatch,
  setStateFiltersOptions?: React.Dispatch<React.SetStateAction<IFilterControlOptions[]>>
) => {
  //filter type is passes from FilterPanel module to UniversalFilter to here, it is a single type/id to denote
  //what the state filters should be (utimately just one of the many set in profile)

  //if the filter exists in the profile, we update it, else we need to add it
  let doAdd = true

  try {
    //user may have no filters to start with, must check for undefined, and if so, set to empty array we can add to, in order to set the filters
    let newStateFilters: {type: string; filters: IFilterControlOptions[]}[] = []
    if (reduxUser?.profile?.filters) {
      newStateFilters = reduxUser?.profile?.filters?.map((f) => {
        const n = {...f}
        if (f.type === filterType) {
          n.filters = [...filters]
          doAdd = false
        }
        return n
      })
    }
    if (doAdd) {
      newStateFilters?.push({type: filterType, filters: filters})
    }
    const updatedProfile = {
      ...reduxUser?.profile,
      filters: newStateFilters,
    }
    reduxUser?.id &&
      reduxUser?.profile &&
      dispatch(
        updateUserAsncThunk({
          id: reduxUser?.id,
          updateUserPart: {profile: updatedProfile},
          originalUser: reduxUser,
        })
      )
    const formatFilters = newStateFilters?.map((f) => {
      const x = formatFiltersDisplay(f.filters)
      const obj = {type: f.type, filters: x}
      return obj
    })
    //check for undefined
    setStateFiltersOptions && setStateFiltersOptions(filters)
    //check for undefined
    formatFilters && setStateFilters(formatFilters)
  } catch (e) {
    const stope = 'here'
  }
}

export class CFilters extends React.Component<any> {
  entity_type: string
  _filterList: any
  filters?: any
  _filterComponent: React.ReactComponentElement<any>
  additional_data?: iAddData

  constructor(
    data: {entity_type: string; filters?: iFilterVals[]; additional_data?: iAddData},
    childComp: {node: React.ReactComponentElement<any>}
  ) {
    super(data)
    //set entity type
    this.entity_type = data.entity_type
    //set possible filter properties
    this._filterList = this.getEntityList(data.entity_type, data.additional_data)
    this.additional_data = data.additional_data
    // map the filters against the list to build the filter object to send
    if (this._filterList?.length && data.filters?.length) {
      const temp: {[key: string]: any} = {}
      //loop the filters
      data.filters.map((f) => {
        //check if it is a property on the entity
        if (this._filterList.toString().includes(f.field)) {
          if (f.comparatorField) {
            temp[`${f.field}${f.comparatorField}`] = f.fieldValue
          } else {
            temp[f.field] = f.fieldValue
          }
        } else {
          //it's another entity so it is entityField and entityValue or a comparator
          if (f.entityField) {
            const entList = f.entityType && this.getEntityList(f.entityType)
            //it's a property of the entity
            if (entList && entList.toString().includes(f.entityField)) {
              if (f.comparatorField) {
                temp[`${f.entityType}__${f.entityField}${f.comparatorField}`] = f.entityValue
              } else {
                temp[`${f.entityType}__${f.entityField}`] = f.entityValue
              }
            }
          } else {
            //its not a property of the entity sent in, or an entity property, so is an exists on the original entity
            temp[`${f.existsField}__${f.fieldValue}`]
          }
        }
      })
      this.filters = temp
    }
    //clone the element to be  able to re-assign and add props for filterControlOptions (built rom this class),
    //and handleDelete (added and assigned in clone here using the passed inReactElements prop for setting the parent components state)
    const clonedElement = cloneElement(childComp.node, {
      filterControlOptions: this._filterList,
      additional_data: this.additional_data,
      handleDelete: (key: string) => {
        //remove from profile, since moving to redux, profile is read only so cannot manipulate the state without using parent save, need
        //to use a copy of the profile then send that to parent save to update the profile
        const profileEntityType =
          childComp.node.props.entity +
          `${childComp.node.props.subEntity ? '|' + childComp.node.props.subEntity : ''}`
        if (
          childComp.node.props.currUser?.profile.filters?.find(
            (t: any) => t.type === profileEntityType
          )
        ) {
          childComp.node.props.currUser?.profile.filters.forEach((item: any) => {
            const copyItem = {...item}
            if (item.type === profileEntityType) {
              const newFitlers = copyItem.filters.map((f: any) => {
                const n = {...f}
                const match = f.fieldOperator && f.fieldName + f.fieldOperator
                if (match === key) {
                  delete n.fieldValue
                  delete n.fieldOperator
                } else {
                  if (f.fieldName.includes('^')) {
                    const forKeyMatch = f.fieldName.split('^')
                    const matchKey = forKeyMatch?.length === 2 ? forKeyMatch[1] : f.fieldName
                    if (matchKey === key) {
                      delete n.fieldValue
                    }
                  } else if (f.fieldName === key || f.fieldName + f.wildcard === key) {
                    delete n.fieldValue
                    //this removes it from pills and filtermodule but not from the profile filters
                  }
                }
                return n
              })
              copyItem.filters = newFitlers
              childComp.node.props.parentSaveFilters &&
                childComp.node.props.parentSaveFilters(copyItem.filters, profileEntityType)
            }
          })
        }
      },
    })
    this._filterComponent = clonedElement
  }

  getEntityList(entity: string, addData?: iAddData) {
    if (entity === 'business') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'name',
        wildcard: '%',
        fieldDisplay: 'Business Name',
        type: 'textbox',
        rank: 1,
      })
      retval.push({
        entityType: entity,
        fieldName: 'sam_identifier',
        fieldDisplay: 'UEI',
        type: 'textbox',
        rank: 2,
      })
      retval.push({
        entityType: entity,
        fieldName: 'city',
        wildcard: '%',
        fieldDisplay: 'Business City',
        type: 'textbox',
        rank: 3,
      })
      retval.push({
        entityType: entity,
        fieldName: 'state_province',
        fieldDisplay: 'Business State',
        type: 'selectbox',
        rank: 4,
        options: stateNameCodes.map((s) => {
          return {key: s.abbr, value: s.stateName}
        }),
      })
      // retval.push({
      //   fieldName: 'country',
      //   fieldDisplay: 'Business Country',
      //   type: 'selectbox',
      //   rank: 5,
      //   options: countries.map((c) => {
      //     return {key: c.code, value: c.label}
      //   }),
      // })
      retval.push({
        entityType: entity,
        fieldName: 'address_postal_code',
        fieldDisplay: 'Postal Code',
        type: 'textbox',
        rank: 6,
      })
      //
      retval.push({
        entityType: entity,
        fieldName: 'congressional_district',
        fieldDisplay: 'Congressional District',
        type: 'textbox',
        rank: 7,
      })

      // retval.push({
      //   fieldName: 'business_structure',
      //   fieldDisplay: 'Business Structure',
      //   type: 'selectbox',
      //   rank: 8,
      //   options: SAMEntityStructure.map((bs) => {
      //     return {key: bs.id, value: bs.name}
      //   }),
      //   fieldDefaultValue: '2',
      // })
      retval.push({
        entityType: entity,
        fieldName: 'first^awards',
        fieldDisplay: 'Award Phase',
        fieldNestedName: 'phase', //so posted filter is actually -> filter: { <fieldName>: {<fieldNestedName>: <fieldValue>}}
        type: 'selectbox',
        rank: 9,
        options: [
          {key: '', value: ''},
          {key: 'phase I', value: 'Phase I'},
          {key: 'phase II', value: 'Phase II'},
        ],
      })
      retval.push({
        entityType: entity,
        fieldName: 'crunchbase_active',
        fieldDisplay: 'Crunchbase Active',
        type: 'checkbox',
        rank: 10,
      })
      // retval.push({
      //   fieldName: 'award__amount',
      //   fieldDisplay: 'Award Amount',
      //   type: 'range',
      //   fieldOperator: '',
      //   rank: 11,
      //   options: [
      //     {key: '', value: ''},
      //     {key: 10000, value: '$10,000'},
      //     {key: 20000, value: '$20,000'},
      //     {key: 50000, value: '$50,000'},
      //     {key: 100000, value: '$100,000'},
      //     {key: 150000, value: '$150,000'},
      //     {key: 200000, value: '$200,000'},
      //     {key: 250000, value: '$250,000'},
      //     {key: 500000, value: '$500,000'},
      //     {key: 1000000, value: '$1,000,000'},
      //     {key: 2000000, value: '$2,000,000'},
      //     {key: 5000000, value: '$5,000,000'},
      //     {key: 10000000, value: '$10,000,000'},
      //     {key: 20000000, value: '$20,000,000'},
      //     {key: 50000000, value: '$50,000,000'},
      //     {key: 250000000, value: '$250,000,000'},
      //     {key: 500000000, value: '$500,000,000'},
      //   ],
      // })
      // retval.push({
      //   fieldName: 'award__count',
      //   fieldDisplay: 'Award Count',
      //   fieldOperator: '',
      //   type: 'range',
      //   rank: 12,
      //   options: [
      //     {key: '', value: ''},
      //     {key: 1, value: '1'},
      //     {key: 2, value: '2'},
      //     {key: 3, value: '3'},
      //     {key: 4, value: '4'},
      //     {key: 5, value: '5'},
      //     {key: 6, value: '6'},
      //     {key: 7, value: '7'},
      //     {key: 8, value: '8'},
      //     {key: 9, value: '9'},
      //     {key: 10, value: '10'},
      //     {key: 15, value: '15'},
      //     {key: 25, value: '25'},
      //     {key: 50, value: '50'},
      //     {key: 100, value: '100'},
      //     {key: 500, value: '500'},
      //     {key: 1000, value: '1000'},
      //   ],
      // })
      retval.push({
        entityType: entity,
        fieldName: 'total_revenue',
        fieldDisplay: 'Total Revenue',
        fieldOperator: '',
        type: 'range',
        rank: 11,
        options: [
          {key: 10000, value: '$10,000'},
          {key: 20000, value: '$20,000'},
          {key: 50000, value: '$50,000'},
          {key: 100000, value: '$100,000'},
          {key: 150000, value: '$150,000'},
          {key: 200000, value: '$200,000'},
          {key: 250000, value: '$250,000'},
          {key: 500000, value: '$500,000'},
          {key: 1000000, value: '$1,000,000'},
          {key: 2000000, value: '$2,000,000'},
          {key: 5000000, value: '$5,000,000'},
          {key: 10000000, value: '$10,000,000'},
          {key: 20000000, value: '$20,000,000'},
          {key: 50000000, value: '$50,000,000'},
          {key: 250000000, value: '$250,000,000'},
          {key: 500000000, value: '$500,000,000'},
        ],
      })
      retval.push({
        entityType: entity,
        fieldName: 'total_funding_usd',
        fieldDisplay: 'Total Funding',
        type: 'range',
        fieldOperator: '',
        rank: 12,
        options: [
          {key: 10000, value: '$10,000'},
          {key: 20000, value: '$20,000'},
          {key: 50000, value: '$50,000'},
          {key: 100000, value: '$100,000'},
          {key: 150000, value: '$150,000'},
          {key: 200000, value: '$200,000'},
          {key: 250000, value: '$250,000'},
          {key: 500000, value: '$500,000'},
          {key: 1000000, value: '$1,000,000'},
          {key: 2000000, value: '$2,000,000'},
          {key: 5000000, value: '$5,000,000'},
          {key: 10000000, value: '$10,000,000'},
          {key: 20000000, value: '$20,000,000'},
          {key: 50000000, value: '$50,000,000'},
          {key: 250000000, value: '$250,000,000'},
          {key: 500000000, value: '$500,000,000'},
        ],
      })

      retval.push({
        entityType: entity,
        fieldName: 'second^awards',
        fieldNestedName: 'id@',
        fieldNestedDefaultValue: '',
        fieldDisplay: 'Has Award',
        type: 'checkbox',
        rank: 13,
      })
      return retval.sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0))
    }
    if (entity === 'innovation') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'name',
        wildcard: '%',
        fieldDisplay: 'Innovation Name',
        type: 'textbox',
        rank: 1,
      })
      retval.push({
        entityType: entity,
        fieldName: 'title_link',
        wildcard: '%',
        fieldDisplay: 'Link Title',
        type: 'textbox',
        rank: 2,
      })
      retval.push({
        entityType: entity,
        fieldName: 'track_code',
        fieldDisplay: 'Track Code',
        type: 'textbox',
        rank: 3,
      })
      retval.push({
        entityType: entity,
        fieldName: 'abstract',
        wildcard: '%',
        fieldDisplay: 'Abstract',
        type: 'textbox',
        rank: 4,
      })
      if (addData) {
        retval.push({
          entityType: entity,
          fieldName: 'multi_university',
          options: addData.values,
          fieldDisplay: 'Select Universities',
          type: 'multichip',
          rank: 5,
        })
      }
      return retval.sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0))
    }
    if (entity === 'opportunity') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'name',
        wildcard: '%',
        fieldDisplay: 'Opportunity Name',
        type: 'textbox',
        rank: 1,
      })
      retval.push({
        entityType: entity,
        fieldName: 'description',
        wildcard: '%',
        fieldDisplay: 'Description',
        type: 'textbox',
        rank: 2,
      })
      retval.push({
        entityType: entity,
        fieldName: 'amount',
        fieldDisplay: 'Amount',
        type: 'range',
        fieldOperator: '',
        rank: 3,
        options: [
          {key: 10000, value: '$10,000'},
          {key: 20000, value: '$20,000'},
          {key: 50000, value: '$50,000'},
          {key: 100000, value: '$100,000'},
          {key: 150000, value: '$150,000'},
          {key: 200000, value: '$200,000'},
          {key: 250000, value: '$250,000'},
          {key: 500000, value: '$500,000'},
          {key: 1000000, value: '$1,000,000'},
          {key: 2000000, value: '$2,000,000'},
          {key: 5000000, value: '$5,000,000'},
          {key: 10000000, value: '$10,000,000'},
          {key: 20000000, value: '$20,000,000'},
          {key: 50000000, value: '$50,000,000'},
          {key: 250000000, value: '$250,000,000'},
          {key: 500000000, value: '$500,000,000'},
        ],
      })
      retval.push({
        entityType: entity,
        fieldName: 'response_deadline_utc',
        fieldDisplay: 'Response Deadline',
        type: 'dategtlt',
        fieldOperator: '',
        rank: 3,
        options: [
          {key: 10000, value: '$10,000'},
          {key: 20000, value: '$20,000'},
          {key: 50000, value: '$50,000'},
          {key: 100000, value: '$100,000'},
          {key: 150000, value: '$150,000'},
          {key: 200000, value: '$200,000'},
          {key: 250000, value: '$250,000'},
          {key: 500000, value: '$500,000'},
          {key: 1000000, value: '$1,000,000'},
          {key: 2000000, value: '$2,000,000'},
          {key: 5000000, value: '$5,000,000'},
          {key: 10000000, value: '$10,000,000'},
          {key: 20000000, value: '$20,000,000'},
          {key: 50000000, value: '$50,000,000'},
          {key: 250000000, value: '$250,000,000'},
          {key: 500000000, value: '$500,000,000'},
        ],
      })
      retval.push({
        entityType: entity,
        fieldName: 'notice_id',
        fieldDisplay: 'Notice Id',
        type: 'textbox',
        rank: 3,
      })
      retval.push({
        entityType: entity,
        fieldName: 'solicitation_number',
        wildcard: '%',
        fieldDisplay: 'Solicitation Number',
        type: 'textbox',
        rank: 4,
      })
      if (addData) {
        retval.push({
          entityType: entity,
          fieldName: 'dept_path',
          options: addData.values,
          fieldDisplay: 'Departments',
          type: 'multichip',
          rank: 5,
        })
      }
      return retval.sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0))
    }
    //#### START AWARD
    if (entity === 'award') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'name',
        wildcard: '%',
        fieldDisplay: 'Award Name',
        type: 'textbox',
        rank: 1,
      })
      retval.push({
        entityType: entity,
        fieldName: 'phase',
        fieldDisplay: 'Award Phase',
        type: 'selectbox',
        rank: 2,
        options: [
          {key: '', value: ''},
          {key: 'Phase I', value: 'Phase I'},
          {key: 'Phase II', value: 'Phase II'},
        ],
      })
      retval.push({
        entityType: entity,
        fieldName: 'topic_code',
        fieldDisplay: 'Topic Code',
        type: 'textbox',
        rank: 3,
      })
      retval.push({
        entityType: entity,
        fieldName: 'year',
        fieldDisplay: 'Award Year',
        type: 'textbox',
        rank: 4,
      })
      retval.push({
        entityType: entity,
        fieldName: 'program',
        fieldDisplay: 'Program',
        type: 'textbox',
        rank: 5,
      })

      retval.push({
        entityType: entity,
        fieldName: 'agency_tracking_number',
        fieldDisplay: 'Agency Tracking Number',
        type: 'textbox',
        rank: 6,
      })
      retval.push({
        entityType: entity,
        fieldName: 'agency_name',
        fieldDisplay: 'Agency Name',
        wildcard: '%',
        type: 'textbox',
        rank: 7,
      })
      retval.push({
        entityType: entity,
        fieldName: 'agency_branch_name',
        fieldDisplay: 'Agency Branch Name',
        wildcard: '%',
        type: 'textbox',
        rank: 8,
      })
      retval.push({
        entityType: entity,
        fieldName: 'amount',
        fieldDisplay: 'Amount',
        type: 'range',
        fieldOperator: '',
        rank: 8,
        options: [
          {key: 10000, value: '$10,000'},
          {key: 20000, value: '$20,000'},
          {key: 50000, value: '$50,000'},
          {key: 100000, value: '$100,000'},
          {key: 150000, value: '$150,000'},
          {key: 200000, value: '$200,000'},
          {key: 250000, value: '$250,000'},
          {key: 500000, value: '$500,000'},
          {key: 1000000, value: '$1,000,000'},
          {key: 2000000, value: '$2,000,000'},
          {key: 5000000, value: '$5,000,000'},
          {key: 10000000, value: '$10,000,000'},
          {key: 20000000, value: '$20,000,000'},
          {key: 50000000, value: '$50,000,000'},
          {key: 250000000, value: '$250,000,000'},
          {key: 500000000, value: '$500,000,000'},
        ],
      })
      return retval.sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0))
    }
    if (entity === 'concept') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'name',
        wildcard: '%',
        fieldDisplay: 'Concept Name',
        type: 'textbox',
        rank: 1,
      })
      return retval
    }
    if (entity === 'person') {
      const retval: Array<IFilterControlOptions> = []
      retval.push({
        entityType: entity,
        fieldName: 'first_name',
        wildcard: '%',
        fieldDisplay: 'First Name',
        type: 'textbox',
        rank: 1,
      })
      retval.push({
        entityType: entity,
        fieldName: 'last_name',
        wildcard: '%',
        fieldDisplay: 'Last Name',
        type: 'textbox',
        rank: 2,
      })
      retval.push({
        entityType: entity,
        fieldName: 'title',
        wildcard: '%',
        fieldDisplay: 'Title',
        type: 'textbox',
        rank: 3,
      })
      retval.push({
        entityType: entity,
        fieldName: 'divider',
        fieldDisplay: 'Location Filters',
        type: 'divider',
        rank: 4,
      })
      retval.push({
        entityType: entity,
        fieldName: 'city',
        fieldDisplay: 'City',
        type: 'textbox',
        rank: 5,
      })
      retval.push({
        entityType: entity,
        fieldName: 'state',
        fieldDisplay: 'State',
        type: 'selectbox',
        rank: 6,
        options: stateNameCodes.map((s) => {
          return {key: s.stateName, value: s.stateName}
        }),
      })
      // retval.push({
      //   fieldName: 'country',
      //   fieldDisplay: 'Country',
      //   type: 'selectbox',
      //   rank: 7,
      //   options: countries.map((c) => {
      //     return {key: c.code, value: c.label}
      //   }),
      // })
      retval.push({
        entityType: entity,
        fieldName: 'postal_code',
        fieldDisplay: 'Postal Code',
        type: 'textbox',
        rank: 8,
      })
      retval.push({
        entityType: entity,
        fieldName: 'divider',
        fieldDisplay: 'Contact Filters',
        type: 'divider',
        rank: 9,
      })
      retval.push({
        entityType: entity,
        fieldName: 'email',
        wildcard: '%',
        fieldDisplay: 'Email',
        type: 'textbox',
        rank: 10,
      })
      retval.push({
        entityType: entity,
        fieldName: 'phone_number',
        fieldDisplay: 'Phone Number',
        type: 'textbox',
        rank: 11,
      })
      return retval.sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0))
    }
    if (entity === 'samEntity') {
      return undefined
    }
  }

  strEnum<T extends string>(o: Array<T>): {[K in T]: K} {
    return o.reduce((res, key) => {
      res[key] = key
      return res
    }, Object.create(null))
  }

  render() {
    return this._filterComponent
  }
}
